// The form model of the Captain Designer (monetization.md §3.1, R1240).
//
// The WARDROOM card edits one draft of the captain looks and sends it
// whole. This module holds the draft and nothing that draws, so a test
// reads it with no DOM. The meta server owns the rule of a name
// (`server/internal/captainlook`): the client pre-checks the rune counts
// of the generated limits only, and a refusal of any other kind comes back
// from the server with the field it names.

import { runeCount } from '../../../util/text';
import {
  CAPTAIN_BACKSTORY_MAX_RUNES, CAPTAIN_NAME_MAX_RUNES, CAPTAIN_NAME_MIN_RUNES,
  CAPTAIN_VOICE_LINE_MAX_RUNES, CAPTAIN_VOICE_MAX_LINES,
} from '../../../gen/balance';
import type { CaptainLooks, WardroomCaptain } from '../../../net/api';
import { GENDER_FEMALE, GENDER_MALE, isFemale, portraitSeed } from '../../portrait/seed';

/** The fields a refusal can name. `portraitGender` is the server's name
 *  of the F/M toggle. */
export type LooksField = 'name' | 'backstory' | 'voice' | 'portraitGender';

/** One draft of the looks of one captain. `voice` always holds
 *  CAPTAIN_VOICE_MAX_LINES boxes; an empty box sends no line. */
export interface LooksDraft {
  name: string;
  seed: number;
  gender: typeof GENDER_MALE | typeof GENDER_FEMALE;
  backstory: string;
  voice: string[];
}

/** One refusal the client found before the save. */
export interface LooksCheck {
  field: LooksField;
  reason: 'short' | 'long';
  /** The limit the text broke, for the sentence. */
  limit: number;
}

/**
 * The seed and the sex the captain shows now: the stored pair where the
 * server holds one, else the face of the name and the talents (R1240).
 */
export function currentFace(c: WardroomCaptain): { seed: number; gender: LooksDraft['gender'] } {
  const seed = c.portraitSeed > 0 ? c.portraitSeed >>> 0 : portraitSeed(c.name, c.talents);
  const gender = c.portraitGender === GENDER_MALE || c.portraitGender === GENDER_FEMALE
    ? c.portraitGender
    : isFemale(c.name) ? GENDER_FEMALE : GENDER_MALE;
  return { seed, gender };
}

/** The draft of a captain as the server answered it. */
export function draftOf(c: WardroomCaptain): LooksDraft {
  const face = currentFace(c);
  const voice = c.voice.slice(0, CAPTAIN_VOICE_MAX_LINES);
  while (voice.length < CAPTAIN_VOICE_MAX_LINES) voice.push('');
  return { name: c.name, seed: face.seed, gender: face.gender, backstory: c.backstory, voice };
}

/** The voice lines a draft sends: trimmed, with the empty boxes left out. */
export function voiceLines(d: LooksDraft): string[] {
  return d.voice.map((l) => l.trim()).filter((l) => l !== '');
}

/** The PUT body of a draft. The seed and the gender go explicit, so the
 *  save stores the face the grid shows. */
export function bodyOf(d: LooksDraft): CaptainLooks {
  return {
    name: d.name.trim(),
    portraitSeed: d.seed >>> 0,
    portraitGender: d.gender,
    backstory: d.backstory.trim(),
    voice: voiceLines(d),
  };
}

/** True when the draft differs from what the server holds. */
export function changed(d: LooksDraft, c: WardroomCaptain): boolean {
  const a = bodyOf(d);
  const b = bodyOf(draftOf(c));
  return JSON.stringify(a) !== JSON.stringify(b);
}

/** The rune-count pre-check of the generated limits, or null. */
export function precheck(d: LooksDraft): LooksCheck | null {
  const name = runeCount(d.name.trim());
  if (name < CAPTAIN_NAME_MIN_RUNES) return { field: 'name', reason: 'short', limit: CAPTAIN_NAME_MIN_RUNES };
  if (name > CAPTAIN_NAME_MAX_RUNES) return { field: 'name', reason: 'long', limit: CAPTAIN_NAME_MAX_RUNES };
  if (runeCount(d.backstory.trim()) > CAPTAIN_BACKSTORY_MAX_RUNES) {
    return { field: 'backstory', reason: 'long', limit: CAPTAIN_BACKSTORY_MAX_RUNES };
  }
  if (voiceLines(d).some((l) => runeCount(l) > CAPTAIN_VOICE_LINE_MAX_RUNES)) {
    return { field: 'voice', reason: 'long', limit: CAPTAIN_VOICE_LINE_MAX_RUNES };
  }
  return null;
}

/** The field a server refusal names, or null for a field the card has
 *  no place for. */
export function refusalField(field: string): LooksField | null {
  return field === 'name' || field === 'backstory' || field === 'voice' || field === 'portraitGender'
    ? field : null;
}

/** A source of random 32-bit words. The browser passes `crypto`. */
export type RandomWords = (out: Uint32Array<ArrayBuffer>) => void;

const cryptoWords: RandomWords = (out) => { crypto.getRandomValues(out); };

/**
 * `n` fresh portrait seeds. A seed is never 0, because 0 means "derive
 * from the name", and never one of `avoid`.
 */
export function freshSeeds(n: number, avoid: readonly number[] = [], rand: RandomWords = cryptoWords): number[] {
  const out: number[] = [];
  const seen = new Set(avoid);
  const buf = new Uint32Array(Math.max(1, n));
  while (out.length < n) {
    rand(buf);
    for (const w of buf) {
      if (out.length >= n) break;
      if (w === 0 || seen.has(w)) continue;
      seen.add(w);
      out.push(w);
    }
  }
  return out;
}

/** The faces of the grid: the current face first, then `total - 1`
 *  fresh seeds. */
export function candidateSeeds(current: number, total: number, rand?: RandomWords): number[] {
  return [current, ...freshSeeds(Math.max(0, total - 1), [current], rand)];
}
